import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, pipe } from 'rxjs';
import { catchError, finalize, filter, switchMap, tap } from 'rxjs/operators';
import { AiAgentChatMappingService } from './ai-agent-chat-mapping.service';
import { AiAgentChatMessage } from './ai-agent-chat-message.models';
import { AiAgentChatSignalrService } from './ai-agent-chat-signalr.service';
import { AiAgentStreamItem } from './ai-agent-stream-item.models';
import { HubConnectionState } from './hub-connection-state';
import { PendingConfirmation } from './pending-confirmation.models';

interface AiAgentChatStoreState {
    messages: AiAgentChatMessage[];
    isOpen: boolean;
    isLoading: boolean;
    conversationId: string | null;
    connectionState: HubConnectionState;
    activeToolName: string | null;
    pendingConfirmation: PendingConfirmation | null;
    streamingContent: string | null;
    streamSubscribed: boolean;
    inputText: string;
}

export const AiAgentChatStore = signalStore(

    withState<AiAgentChatStoreState>({
        messages: [],
        isOpen: false,
        isLoading: false,
        conversationId: null,
        connectionState: 'disconnected',
        activeToolName: null,
        pendingConfirmation: null,
        streamingContent: null,
        streamSubscribed: false,
        inputText: '',
    }),

    withComputed((store) => ({
        hasMessages: computed(() => store.messages().length > 0),
        isConnected: computed(() => store.connectionState() === 'connected'),
        isReconnecting: computed(() => store.connectionState() === 'reconnecting'),
        canSend: computed(() => !!store.inputText().trim() && store.connectionState() === 'connected' && !store.isLoading()),
        isThinking: computed(() => store.isLoading() && !store.streamingContent()),
        showEmptyState: computed(() => !store.messages().length && !store.isLoading()),
        isInputDisabled: computed(() => store.connectionState() !== 'connected' || store.isLoading()),
    })),

    withMethods((
        store,
        signalrService = inject(AiAgentChatSignalrService),
        mappingService = inject(AiAgentChatMappingService)
    ) => {
        function applyStreamItem(item: AiAgentStreamItem): void {
            switch (item.type) {
                case 'user-message':
                    // Already added optimistically — skip echo
                    break;

                case 'tool-progress':
                    if (item.status === 'executing') {
                        patchState(store, {
                            activeToolName: item.toolName ?? null,
                            messages: [...store.messages(), mappingService.toToolProgressMessage(item)],
                        });
                    } else {
                        patchState(store, { activeToolName: null });
                    }
                    break;

                case 'assistant-token':
                    patchState(store, {
                        streamingContent: (store.streamingContent() ?? '') + (item.delta ?? ''),
                    });
                    break;

                case 'assistant-message-end': {
                    const content = store.streamingContent() ?? '';
                    if (content) {
                        patchState(store, {
                            messages: [...store.messages(), mappingService.toAssistantMessage(content, item.timestamp)],
                            streamingContent: null,
                            isLoading: false,
                            activeToolName: null,
                        });
                    } else {
                        patchState(store, { isLoading: false, activeToolName: null });
                    }
                    break;
                }

                case 'confirmation-required':
                    patchState(store, {
                        pendingConfirmation: mappingService.toPendingConfirmation(item),
                        messages: [...store.messages(), mappingService.toConfirmationMessage(item)],
                    });
                    break;

                case 'error':
                    patchState(store, {
                        isLoading: false,
                        activeToolName: null,
                        streamingContent: null,
                        messages: [...store.messages(), mappingService.toErrorMessage(item)],
                    });
                    break;
            }
        }

        const _streamSubscriptionRx = rxMethod<string>(
            pipe(
                switchMap(conversationId =>
                    signalrService.getConversationStream(conversationId).pipe(
                        tap(item => applyStreamItem(item)),
                        catchError(() => {
                            patchState(store, { isLoading: false, streamSubscribed: false });
                            return EMPTY;
                        }),
                        finalize(() => patchState(store, { streamSubscribed: false })),
                    ),
                ),
            ),
        );

        const messageSentCallbacks: Array<() => void> = [];

        return {
            _dispose(): void {
                messageSentCallbacks.length = 0;
            },

            togglePanel(): void {
                patchState(store, { isOpen: !store.isOpen() });
            },

            openPanel(): void {
                patchState(store, { isOpen: true });
            },

            setInputText(value: string): void {
                patchState(store, { inputText: value });
            },

            registerOnMessageSent(callback: () => void): void {
                messageSentCallbacks.push(callback);
            },

            sendMessageRx: rxMethod<string>(
                pipe(
                    filter(text => !!text.trim() && !store.isLoading()),
                    tap(text => {
                        let conversationId = store.conversationId();
                        if (!conversationId) {
                            conversationId = crypto.randomUUID();
                            patchState(store, { conversationId });
                        }
                        patchState(store, {
                            messages: [...store.messages(), {
                                id: crypto.randomUUID(),
                                role: 'user' as const,
                                content: text.trim(),
                                timestamp: new Date(),
                            }],
                            isLoading: true,
                        });
                        if (!store.streamSubscribed()) {
                            patchState(store, { streamSubscribed: true });
                            _streamSubscriptionRx(store.conversationId()!);
                        }
                        patchState(store, { inputText: '' });
                        messageSentCallbacks.forEach(cb => cb());
                    }),
                    switchMap(text =>
                        signalrService.sendMessage(store.conversationId()!, text.trim()).pipe(
                            catchError(err => {
                                console.error('[AiAgentChatStore] sendMessage failed', err);
                                patchState(store, {
                                    isLoading: false,
                                    messages: [...store.messages(), {
                                        id: crypto.randomUUID(),
                                        role: 'assistant' as const,
                                        content: 'Failed to send message. Please try again.',
                                        timestamp: new Date(),
                                    }],
                                });
                                return EMPTY;
                            }),
                        ),
                    ),
                ),
            ),

            confirmActionRx: rxMethod<{ actionId: string; confirmed: boolean }>(
                pipe(
                    tap(({ actionId, confirmed }) => {
                        patchState(store, {
                            pendingConfirmation: null,
                            messages: store.messages().map(m =>
                                m.role === 'confirmation' && m.actionId === actionId
                                    ? { ...m, confirmed }
                                    : m,
                            ),
                        });
                    }),
                    switchMap(({ actionId, confirmed }) => {
                        const conversationId = store.conversationId();
                        if (!conversationId) return EMPTY;
                        return signalrService.confirmAction(conversationId, actionId, confirmed).pipe(
                            catchError(err => {
                                console.error('[AiAgentChatStore] confirmAction failed', err);
                                return EMPTY;
                            }),
                        );
                    }),
                ),
            ),

            clearConversationRx: rxMethod<void>(
                pipe(
                    switchMap(() => {
                        const conversationId = store.conversationId();
                        patchState(store, {
                            messages: [],
                            conversationId: null,
                            streamingContent: null,
                            isLoading: false,
                            activeToolName: null,
                            pendingConfirmation: null,
                            streamSubscribed: false,
                        });
                        if (!conversationId) return EMPTY;
                        return signalrService.clearConversation(conversationId).pipe(
                            catchError(() => EMPTY),
                        );
                    }),
                ),
            ),

            _onConnectionStateChangeRx: rxMethod<HubConnectionState>(
                pipe(tap(state => patchState(store, { connectionState: state }))),
            ),
        };
    }),

    withHooks((
        store,
        signalrService = inject(AiAgentChatSignalrService)
    ) => ({
        onInit: () => {
            signalrService.connect();
            store._onConnectionStateChangeRx(signalrService.connectionState$);
        },
        onDestroy: () => {
            store._dispose();
            signalrService.disconnect();
        },
    })),
);
